import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Check, X, Paperclip, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RichTextEditor, RichTextView } from "@/components/forms/RichTextEditor";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AuthorLink } from "@/components/social/AuthorLink";
import { timeAgo } from "@/lib/timeago";

const MESSAGE_MAX = 500;
const PLAIN = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim().length;

const statusBadge = (s) => {
  if (s === "accepted") return <Badge className="bg-teal-50 text-teal-700">Accepted</Badge>;
  if (s === "declined") return <Badge variant="secondary">Declined</Badge>;
  return <Badge variant="secondary">Pending</Badge>;
};

// SEC-01: pitch decks are private; download via the authenticated api client (sends the
// session token) as a blob instead of a public link, then save it locally.
function AttachmentLink({ attachment, testId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const rel = attachment.url.replace(/^\/api/, "");
      const res = await api.get(rel, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = attachment.name || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      const code = e?.response?.status;
      toast.error(code === 401 ? "Please log in to download this file."
        : code === 403 ? "You don't have access to this file."
        : "Couldn't download the file.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={download} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline disabled:opacity-60" data-testid={testId}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} {attachment.name || "Attachment"}
    </button>
  );
}

function RespondDialog({ inq, action, onDone }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const plain = PLAIN(message);
    if (plain > MESSAGE_MAX) { toast.error(`Note must be ${MESSAGE_MAX} characters or fewer.`); return; }
    setBusy(true);
    try { await api.post(`/inquiries/${inq.id}/respond`, { action, message: plain > 0 ? message : "" }); toast.success(action === "accept" ? "Accepted" : "Declined"); setOpen(false); onDone(); }
    catch (e) { toast.error("Action failed"); } finally { setBusy(false); }
  };
  return (
    <>
      {action === "accept"
        ? <Button size="sm" onClick={() => setOpen(true)} data-testid={`accept-inq-${inq.id}`}><Check className="mr-1.5 h-4 w-4" /> Accept</Button>
        : <Button size="sm" variant="secondary" onClick={() => setOpen(true)} data-testid={`decline-inq-${inq.id}`}><X className="mr-1.5 h-4 w-4" /> Decline</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{action === "accept" ? "Accept" : "Decline"} inquiry</DialogTitle><DialogDescription>Send an optional note with your response.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Note <span className="font-[440] text-muted-foreground">(optional)</span></Label>
              <span className={`text-xs tabular-nums ${PLAIN(message) > MESSAGE_MAX ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid={`respond-note-count-${inq.id}`}>{PLAIN(message)}/{MESSAGE_MAX}</span>
            </div>
            <RichTextEditor value={message} onChange={setMessage} toolbar={["bold", "italic", "underline", "link"]} placeholder="Add a note (optional)…" testId={`respond-note-${inq.id}`} />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || PLAIN(message) > MESSAGE_MAX} data-testid={`respond-submit-${inq.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Inquiries() {
  const [inbox, setInbox] = useState(null);
  const [sent, setSent] = useState(null);

  const load = async () => {
    try {
      const [i, s] = await Promise.all([api.get("/inquiries/inbox"), api.get("/inquiries/sent")]);
      setInbox(i.data); setSent(s.data);
    } catch (e) { setInbox([]); setSent([]); }
  };
  useEffect(() => { load(); }, []);

  if (!inbox || !sent) return <Loading />;
  const pendingCount = inbox.filter((x) => x.status === "pending").length;

  return (
    <div>
      <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Inquiries</h1>
      <p className="mt-1 text-muted-foreground">Meeting and pitch-deck requests for your businesses and capital profiles.</p>

      <Tabs defaultValue="inbox" className="mt-6">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-i-inbox">Received{pendingCount > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{pendingCount}</Badge>}</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-i-sent">Sent ({sent.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-4">
          {inbox.length === 0 ? (
            <EmptyState icon={Inbox} title="No inquiries yet" description="Meeting and pitch-deck requests will appear here." testId="i-inbox-empty" />
          ) : (
            <div className="space-y-3">
              {inbox.map((q) => (
                <Card key={q.id} className="p-5" data-testid={`i-inbox-${q.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-9 w-9">
                        {q.from && q.from.headshot_url && <AvatarImage src={fileUrl(q.from.headshot_url)} alt="" />}
                        <AvatarFallback className="bg-secondary text-xs">{(q.from && q.from.display_name ? q.from.display_name[0] : "?")}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm font-[600] text-foreground"><AuthorLink card={q.from} className="text-sm font-[600] text-foreground" fallback="Member" testId={`inquiry-from-link-${q.id}`} /></div>
                        <div className="text-xs text-muted-foreground">{q.label} · {q.target ? q.target.display_name : ""}</div>
                      </div>
                    </div>
                    {statusBadge(q.status)}
                  </div>
                  {q.message && <RichTextView html={q.message} className="mt-3" />}
                  {q.attachment && q.attachment.url && (
                    <AttachmentLink attachment={q.attachment} testId={`inquiry-attachment-${q.id}`} />
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    {q.status === "pending" ? (
                      <>
                        <RespondDialog inq={q} action="accept" onDone={load} />
                        <RespondDialog inq={q} action="decline" onDone={load} />
                      </>
                    ) : q.response_message ? <div className="text-sm text-muted-foreground"><span className="mr-1">Your note:</span><RichTextView html={q.response_message} className="inline text-muted-foreground" /></div> : null}
                    <span className="ml-auto text-xs text-muted-foreground">{timeAgo(q.created_at)}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="mt-4">
          {sent.length === 0 ? (
            <EmptyState icon={Inbox} title="No sent inquiries" description="Requests you send to businesses and capital profiles appear here." testId="i-sent-empty" />
          ) : (
            <div className="space-y-3">
              {sent.map((q) => (
                <Card key={q.id} className="p-5" data-testid={`i-sent-${q.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-[600] text-foreground">{q.label} · {q.target ? q.target.display_name : ""}</div>
                    {statusBadge(q.status)}
                  </div>
                  {q.message && <RichTextView html={q.message} className="mt-2" />}
                  {q.attachment && q.attachment.url && (
                    <AttachmentLink attachment={q.attachment} testId={`sent-attachment-${q.id}`} />
                  )}
                  {q.response_message && <div className="mt-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground"><span className="font-[600]">Response:</span> <RichTextView html={q.response_message} className="mt-1" /></div>}
                  <p className="mt-2 text-xs text-muted-foreground">{timeAgo(q.created_at)}</p>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
